import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, withStyles, makeStyles, Paper, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import styled from 'styled-components';

const useStyles = makeStyles({
  selected: {
    border: '1px solid #e9e9e9',
    backgroundColor: '#eaddec',
  },
  selectedDark: {
    border: '1px solid #5c5c5c',
    backgroundColor: '#5e176a',
  },
  row: {
    border: '1px solid #e9e9e9',
  },
  rowDark: {
    border: '1px solid #5c5c5c',
    backgroundColor: '#2d3436',
  },
  typographyDark: {
    color: '#bebebe',
  },
  typographyDarkSelected: {
    color: '#e1bee7',
  },
  typographySpecialDarkSelected: {
    color: '#e1bee7',
    '&:after': {
      content: '\\a',
      whiteSpace: 'pre',
    },
  },
  typographySpecialDark: {
    color: '#e1bee7',
    '&:after': {
      content: '\\a',
      whiteSpace: 'pre',
    },
  },
  typographySpecial: {
    '&:after': {
      content: '\\a',
      whiteSpace: 'pre',
    },
  },
  header: {
    backgroundColor: '#f5f7f7',
    border: '1px solid #e9e9e9',
    padding: '5px 10px 5px 10px !important',
  },
  headerDark: {
    backgroundColor: '#1c1c1c',
    border: '1px solid #5c5c5c',
    padding: '5px 10px 5px 10px !important',
  },
});

const SpecialCases = [
  {
    id: 'onsales',
    autoprice_interval: 5,
    autoprice_logic: 'Onsales:',
    autoprice_logic2: '3D Prior - 4D Post',
  },
  {
    id: 'ga',
    autoprice_interval: 15,
    autoprice_logic: '500+ GA Tickets &',
    autoprice_logic2: '30+ DTE',
  },
];

const IntervalTooltip = ({ classes, rows, children, selected, event }) => {
  const theme = useTheme();
  const customClasses = useStyles();
  const tdClass = interval => {
    if (parseInt(interval, 10) === parseInt(selected, 10) && !event.is_onsale && !event.is_ga) {
      return theme.palette.type === 'light' ? customClasses.selected : customClasses.selectedDark;
    }
    return theme.palette.type === 'light' ? customClasses.row : customClasses.rowDark;
  };

  const tdClassSpecial = id => {
    if (id === 'ga' && event.is_ga && !event.is_onsale) {
      return theme.palette.type === 'light' ? customClasses.selected : customClasses.selectedDark;
    }
    if (id === 'onsales' && event.is_onsale) {
      return theme.palette.type === 'light' ? customClasses.selected : customClasses.selectedDark;
    }
    return theme.palette.type === 'light' ? customClasses.row : customClasses.rowDark;
  };

  const typographyClass = interval => {
    if (parseInt(interval, 10) === parseInt(selected, 10) && !event.is_onsale && !event.is_ga) {
      return theme.palette.type === 'light' ? customClasses.typography : customClasses.typographyDarkSelected;
    }
    return theme.palette.type === 'light' ? customClasses.typography : customClasses.typographyDark;
  };

  const typographyClassSpecial = id => {
    if (id === 'ga' && event.is_ga && !event.is_onsale) {
      return theme.palette.type === 'light'
        ? customClasses.typographySpecial
        : customClasses.typographySpecialDarkSelected;
    }
    if (id === 'onsales' && event.is_onsale) {
      return theme.palette.type === 'light'
        ? customClasses.typographySpecial
        : customClasses.typographySpecialDarkSelected;
    }
    return theme.palette.type === 'light' ? customClasses.typographySpecial : customClasses.typographySpecialDark;
  };

  const intervalLabel = row => {
    if (row.high_day && row.low_day !== row.high_day) {
      return `${row.low_day} - ${row.high_day} DTE`;
    }
    if (!row.high_day && row.low_day) {
      return `${row.low_day}+ DTE`;
    }
    if (row.high_day && row.low_day === row.high_day) {
      return row.low_day;
    }
    return '';
  };

  const renderTable = items => (
    <Paper>
      <Table style={{ padding: '10px' }}>
        <tbody>
          <tr>
            <Th className={theme.palette.type === 'light' ? customClasses.header : customClasses.headerDark}>
              <Typography>Autoprice Logic</Typography>
            </Th>
            <Th className={theme.palette.type === 'light' ? customClasses.header : customClasses.headerDark}>
              <Typography>Autopricing Interval</Typography>
            </Th>
          </tr>
          {items.map(row => (
            <tr key={row.id}>
              <Td className={tdClass(row.autoprice_interval)}>
                <Typography className={typographyClass(row.autoprice_interval)}>{intervalLabel(row)}</Typography>
              </Td>
              <Td className={tdClass(row.autoprice_interval)}>
                <Typography className={typographyClass(row.autoprice_interval)}>
                  {`${row.autoprice_interval} min`}
                </Typography>
              </Td>
            </tr>
          ))}
          <tr>
            <Th
              colSpan={2}
              className={theme.palette.type === 'light' ? customClasses.header : customClasses.headerDark}
            >
              <Typography>Special Cases</Typography>
            </Th>
          </tr>
          {SpecialCases.map(row => (
            <tr key={row.id}>
              <Td className={tdClassSpecial(row.id)}>
                <Typography className={typographyClassSpecial(row.id)}>{row.autoprice_logic}</Typography>
                <Typography className={typographyClassSpecial(row.id)}>{row.autoprice_logic2}</Typography>
              </Td>
              <Td className={tdClassSpecial(row.id)}>
                <Typography className={typographyClassSpecial(row.id)}>{`${row.autoprice_interval} min`}</Typography>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Paper>
  );

  return (
    <Tooltip
      classes={{
        tooltip: classes.htmlTooltip,
      }}
      title={renderTable(rows)}
      placement="right"
      arrow
    >
      {children}
    </Tooltip>
  );
};

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  padding: 15px;
`;

const Th = styled.th`
  border-collapse: collapse;
  text-align: center;
  vertical-align: center;
  padding: 5px;
`;

const Td = styled.td`
  border-collapse: collapse;
  text-align: left;
  vertical-align: center;
  padding: 5px;
`;

IntervalTooltip.propTypes = {
  classes: PropTypes.object.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      days: PropTypes.string,
      intervals: PropTypes.number,
    })
  ).isRequired,
  children: PropTypes.element.isRequired,
  selected: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
};

const styles = () => ({
  htmlTooltip: {
    backgroundColor: 'rgba(0, 0, 0, 0)',
  },
});

export default withStyles(styles)(IntervalTooltip);
